import { Box, Divider, Stack, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { AssignmentActivitySummaryTable } from "./components/AssignmentActivitySummaryTable";
import { DetailedReportTable } from "./components/DetailedReportTable";
import { exportToPDF } from "../utils/exportToPdf";
import { exportToExcel } from "./utils/exportToExcel";
import {
  appliedFiltersType,
  AssignmentRecord,
  assignmentActivityByUserReportType,
  assignmentActivityReportByPortalsType,
  assignmentActivityReportCompleteType,
  ReportTemplateBodyParams,
  ReportTemplateTypeEnum,
  summaryType,
  userAssignments,
} from "../../../../services/api/types/reports/reports";
import { useEffect, useState } from "react";
import {
  previewReportTemplate,
  runReportTemplate,
} from "../../../../services/api/apiClient";
import { usePortal } from "../../../../context/PortalsContext";
import { useAuth } from "../../../../context/AuthContext";
import {
  formatDate,
  formatTime,
  generateCertificatesFileName,
  generateFileName,
} from "../utils/helpers";
import { Spinner } from "../../../../components/Spinner/Spinner";
import { ExportMenu } from "../components/ExportMenu";
import { useLocation, useParams } from "react-router-dom";
import { DetailedReportByPortalTable } from "./components/DetailedReportByPortalTable";
import Logo from "../../../../authentication/components/Logo";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { ExpandableFiltersSection } from "../components/ExpandableFiltersSection";
import { ReportStatsCards } from "./components/ReportStatsCards";
import { useToast } from "../../../../context/ToastContext";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { UserCertificateResult } from "../../../../services/api/types/certificates/certificates";
import { AssignmentType } from "../../../../services/api/types/general/homeAssignment";
import { CertificateView } from "../../../Certificates/components/CertificateView";

import { usePageCustomisation } from "../../../../hooks/usePageCustomisation";

interface AssignmentActivityReportProps {
  previewTemplateData?: ReportTemplateBodyParams;
  previewPortalId?: string;
  isPreviewMode?: boolean;
}

export const AssignmentActivityReport: React.FC<
  AssignmentActivityReportProps
> = ({ previewTemplateData, previewPortalId, isPreviewMode }) => {
  const [data, setData] = useState<assignmentActivityReportCompleteType | null>(
    null,
  );
  const [appliedFilters, setAppliedFilters] = useState<appliedFiltersType>({});
  const [loading, setLoading] = useState(false);
  const [isGroupedByPortal, setIsGroupedByPortal] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [certForDownload, setCertForDownload] = useState<{
    certificate: UserCertificateResult;
    recipientName: string;
  } | null>(null);
  const [certificatesLoading, setCertificatesLoading] = useState(false);

  const { portals } = usePortal();
  const { auth } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const { portalUid: urlPortalUid } = useParams();
  usePageCustomisation("HOME");

  const { customisations } = useCustomisation();
  const {
    GPC: { navbarSettings },
  } = customisations.customisations;

  const gpcNavbarBgColor = navbarSettings?.navbarBgColor || undefined;

  // Check if preview mode: prop OR location.state
  const previewMode =
    isPreviewMode ||
    (previewTemplateData !== undefined && previewPortalId !== undefined) ||
    (location.state as { previewMode?: boolean })?.previewMode ||
    false;

  const currentDate = new Date();
  const formattedDate = formatDate(currentDate);
  const formattedTime = formatTime(currentDate);

  // Get user name from auth context
  const userName = auth.user
    ? `${auth.user.firstName || ""} ${auth.user.lastName || ""}`.trim() ||
      "Unknown User"
    : "Unknown User";

  useEffect(() => {
    if (previewMode) {
      const templateData =
        previewTemplateData ||
        (location.state as { templateData?: ReportTemplateBodyParams })
          ?.templateData;
      const portalId =
        previewPortalId || (location.state as { portalId?: string })?.portalId;

      if (!templateData || !portalId) {
        console.error("Missing template data or portalId in preview mode");
        return;
      }
      fetchPreviewData(portalId, templateData);
    } else {
      const reportTemplateId = localStorage.getItem("currentReportTemplateId");
      const portalId = urlPortalUid || portals.selectedPortal?.portalUid;

      if (!portalId || !reportTemplateId) {
        console.error("Missing portalId or reportTemplateId");
        return;
      }

      fetchData(portalId, reportTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlPortalUid,
    portals.selectedPortal,
    previewMode,
    previewTemplateData,
    previewPortalId,
    location.state,
  ]);

  const fetchPreviewData = async (
    portalId: string,
    templateData: ReportTemplateBodyParams,
  ) => {
    if (!portalId || !templateData) return;
    setLoading(true);

    try {
      const response = await previewReportTemplate(portalId, templateData);

      if (!response.finResult) {
        console.error("Response does not have finResult property");
        setData(null);
        setIsGroupedByPortal(false);
        return;
      }

      // Extract appliedFilters from response
      if (response.finResult?.appliedFilters) {
        setAppliedFilters(response.finResult.appliedFilters);
      }

      // Detect structure: if first item has 'users' property, it's grouped by portal
      const firstItem = response?.finResult?.reportData[0];
      setIsGroupedByPortal(
        "users" in firstItem && Array.isArray(firstItem.users),
      );
      setData(response);
    } catch (error: any) {
      console.error("Failed to fetch preview data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (portalId: string, reportTemplateId: string) => {
    if (!portalId || !reportTemplateId) return;
    setLoading(true);

    try {
      const response = await runReportTemplate(
        portalId,
        reportTemplateId,
        auth.user?.UserPortalUid || "",
      );

      // Set timestamp to notify Reports page that a report was run
      const now = Date.now();
      const last = parseInt(
        localStorage.getItem("reportRunTimestamp") || "0",
        10,
      );
      const next = now > last ? now : last + 1;
      localStorage.setItem("reportRunTimestamp", next.toString());

      if (!response.finResult) {
        console.error("Response does not have finResult property");
        setData(null);
        setIsGroupedByPortal(false);
        return;
      }

      // Extract appliedFilters from response
      if (response.finResult?.appliedFilters) {
        setAppliedFilters(response.finResult.appliedFilters);
      }

      // Detect structure: if first item has 'users' property, it's grouped by portal
      const firstItem = response.finResult.reportData[0];
      setIsGroupedByPortal(
        "users" in firstItem && Array.isArray(firstItem.users),
      );
      setData(response as assignmentActivityReportCompleteType);
    } catch (error: any) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setFiltersExpanded(true); // Ensure filters are expanded before export

    await new Promise((resolve) => setTimeout(resolve, 150)); // Wait for filters to expand

    // Finally call the export function
    await exportToPDF(
      "assignment-activity-report",
      generateFileName(ReportTemplateTypeEnum.ASSIGNMENT_ACIVITY_REPORT),
    );
  };

  const handleExportExcel = () => {
    exportToExcel({
      reportType: "Assignment activity report",
      generatedBy: userName,
      time: formattedTime,
      filters: appliedFilters,
      detailedData: data?.finResult?.reportData as
        | assignmentActivityByUserReportType[]
        | assignmentActivityReportByPortalsType[],
      summaryData: data?.finResult?.summary as summaryType[],
      isGroupedByPortal: isGroupedByPortal,
      fileName: generateFileName(
        ReportTemplateTypeEnum.ASSIGNMENT_ACIVITY_REPORT,
      ),
    });
  };

  const extractCertificateEntries = (): {
    certificate: UserCertificateResult;
    recipientName: string;
  }[] => {
    if (!data?.finResult?.reportData) return [];

    const entries: {
      certificate: UserCertificateResult;
      recipientName: string;
    }[] = [];

    const pushEntryIfCertified = (
      assignment: userAssignments,
      name: string,
    ) => {
      const records = assignment.records ?? [];

      if (records.length === 0) {
        if (assignment.certificateUid) {
          entries.push({
            recipientName: name,
            certificate: {
              certificateUid: assignment.certificateUid,
              assignment: assignment.title,
              type: assignment.type as AssignmentType,
              startDate: assignment.startDate,
              completionDate: assignment.completionDate,
              timeSpent: assignment.timeSpent,
              bestScore: assignment.bestScore,
            },
          });
        }
        return;
      }

      records.forEach((record: AssignmentRecord) => {
        if (record.certificateUid) {
          entries.push({
            recipientName: name,
            certificate: {
              certificateUid: record.certificateUid,
              assignment: assignment.title,
              type: assignment.type as AssignmentType,
              startDate: record.startDate ?? assignment.startDate,
              completionDate:
                record.completionDate ?? assignment.completionDate,
              timeSpent: record.timeSpent ?? assignment.timeSpent,
              bestScore: record.bestScore ?? assignment.bestScore,
            },
          });
        }
      });
    };

    if (isGroupedByPortal) {
      const portalData = data.finResult
        .reportData as assignmentActivityReportByPortalsType[];
      for (const portal of portalData) {
        for (const user of portal.users) {
          const name =
            `${user.firstName} ${user.lastName}`.trim() || user.email;
          for (const assignment of user.assignments) {
            pushEntryIfCertified(assignment, name);
          }
        }
      }
    } else {
      const userData = data.finResult
        .reportData as assignmentActivityByUserReportType[];
      for (const user of userData) {
        const name = `${user.firstName} ${user.lastName}`.trim() || user.email;
        for (const assignment of user.assignments) {
          pushEntryIfCertified(assignment, name);
        }
      }
    }

    return entries;
  };

  const generateSingleCertPDF = async (
    certificate: UserCertificateResult,
    recipientName: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const safeName = recipientName.replace(/[^a-z0-9]/gi, "_");
    const safeAssignment = certificate.assignment.replace(/[^a-z0-9]/gi, "_");
    const filename = `${safeName}_${safeAssignment}_${certificate.certificateUid}_certificate.pdf`;

    setCertForDownload({ certificate, recipientName });
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const element = document.getElementById(certificate.certificateUid);
      if (!element) {
        throw new Error(
          `Certificate element not found: ${certificate.certificateUid}`,
        );
      }

      const images = element.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map((img) => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
            setTimeout(() => resolve(), 5000);
          });
        }),
      );
      await new Promise((resolve) => setTimeout(resolve, 200));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = (canvas.height * pageWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);

      return { blob: pdf.output("blob"), filename };
    } finally {
      setCertForDownload(null);
    }
  };

  const handleExportCertificates = async () => {
    setCertificatesLoading(true);
    try {
      const entries = extractCertificateEntries();

      if (entries.length === 0) {
        showToast(
          "No certificates found for the current report filters.",
          "warning",
        );
        return;
      }

      if (entries.length === 1) {
        const { blob, filename } = await generateSingleCertPDF(
          entries[0].certificate,
          entries[0].recipientName,
        );
        saveAs(blob, filename);
      } else {
        const zip = new JSZip();
        for (let i = 0; i < entries.length; i++) {
          const { blob, filename } = await generateSingleCertPDF(
            entries[i].certificate,
            entries[i].recipientName,
          );
          zip.file(filename, blob);
          if (i < entries.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `${generateCertificatesFileName()}.zip`);
      }
      showToast("Certificates downloaded successfully.", "success");
    } catch (error) {
      console.error("Error downloading certificates:", error);
      showToast("Failed to download certificates. Please try again.", "error");
    } finally {
      setCertificatesLoading(false);
    }
  };

  return loading ? (
    <Spinner />
  ) : (
    <>
      {certForDownload && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            overflow: "hidden",
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#2B2B2B",
              minHeight: "100vh",
              py: 6,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CertificateView
              certificate={certForDownload.certificate}
              recipientName={certForDownload.recipientName}
            />
          </Box>
        </Box>
      )}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          py: { xs: 0, md: 4 },
          justifyContent: "center",
        }}
      >
        <Box
          id="assignment-activity-report"
          sx={{
            backgroundColor: "#fff",
            width: { xs: "100%", md: "1315px" },
            p: { xs: 2, md: 3 },
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{ backgroundColor: gpcNavbarBgColor, p: 1 }}
          >
            <Logo isLoginPageLogo={false} />
            {previewMode ? (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#667085",
                }}
              >
                Preview mode
              </Typography>
            ) : (
              <ExportMenu
                className="export-buttons"
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onExportCertificates={handleExportCertificates}
                isCertificatesLoading={certificatesLoading}
                singleCertificateExport={false}
              />
            )}
          </Stack>

          <Divider sx={{ borderColor: "#5354D8", mb: 2, mt: 3 }} />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="start"
            mb={2}
          >
            <Typography
              sx={{ color: "#1F2937", fontWeight: 600, fontSize: 20 }}
            >
              Assignment Activity Report
            </Typography>

            <Stack spacing={0.5} alignItems="start">
              <Box display="flex" alignItems="center" gap={0.75}>
                <CalendarTodayOutlinedIcon
                  sx={{ fontSize: 12, color: "#667085" }}
                />
                <Typography sx={{ fontSize: 10, color: "#667085" }}>
                  {formattedDate}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={0.75}>
                <AccessTimeOutlinedIcon
                  sx={{ fontSize: 12, color: "#667085" }}
                />
                <Typography sx={{ fontSize: 12, color: "#667085" }}>
                  {formattedTime}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={0.75}>
                <PersonOutlinedIcon sx={{ fontSize: 12, color: "#667085" }} />
                <Typography sx={{ fontSize: 10, color: "#667085" }}>
                  {userName}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ borderColor: "#5354D8", mb: 2 }} />

          {/* Filters Section */}
          <ExpandableFiltersSection
            appliedFilters={appliedFilters}
            expanded={filtersExpanded}
            onExpandedChange={setFiltersExpanded}
          />

          {/* Stats Cards */}
          <ReportStatsCards summary={data?.finResult?.summary || []} />

          {/* Learning Path Summary Table */}
          <Typography fontSize={20} fontWeight={600} color="#1F2937">
            Summary
          </Typography>
          <Typography fontSize={14} color="#475467" mb={1}>
            Completion breakdown per assignment
          </Typography>
          <Divider sx={{ borderColor: "#5354D8", mb: 2 }} />
          <AssignmentActivitySummaryTable
            data={data?.finResult?.summary || []}
          />

          {/* Deatiled Report Table */}
          <Typography fontSize={20} fontWeight={600} color="#1F2937" mt={3}>
            Details
          </Typography>
          <Typography fontSize={14} color="#475467" mb={1}>
            Individual assignment records grouped by employee
          </Typography>
          <Divider sx={{ borderColor: "#5354D8", mb: 2 }} />
          {isGroupedByPortal ? (
            <DetailedReportByPortalTable
              data={
                data?.finResult
                  .reportData as assignmentActivityReportByPortalsType[]
              }
            />
          ) : (
            <DetailedReportTable
              data={
                data?.finResult
                  ?.reportData as assignmentActivityByUserReportType[]
              }
            />
          )}
        </Box>
      </Box>
    </>
  );
};
